import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  PlusOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  certificateOfConformityService,
  type CertificateOfConformityPayload,
  type CertificateOfConformityRecord,
} from '@/services/certificate-of-conformity.service';
import settingsService from '@/services/settingsService';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;
const { Search, TextArea } = Input;

interface CertificateOfConformityFormValues {
  document_number: string;
  document_revision?: string;
  date: dayjs.Dayjs;
  job_card_number: string;
  po_number?: string;
  job_number?: string;
  supplier_po_number?: string;
  heat_number?: string;
  part_number: string;
  part_description: string;
  part_revision_level?: string;
  date_of_completion: dayjs.Dayjs;
  quantity: number;
  qc_engineer: string;
  notes?: string;
}

const emptyToNull = (value?: string): string | null => {
  const next = value?.trim();
  return next ? next : null;
};

const mapFormToPayload = (
  values: CertificateOfConformityFormValues,
  options?: { includeDocumentNumber?: boolean }
): CertificateOfConformityPayload => ({
  ...(options?.includeDocumentNumber && values.document_number.trim()
    ? { document_number: values.document_number.trim() }
    : {}),
  document_revision: emptyToNull(values.document_revision),
  date: values.date.format('YYYY-MM-DD'),
  job_card_number: values.job_card_number.trim(),
  po_number: emptyToNull(values.po_number),
  job_number: emptyToNull(values.job_number),
  supplier_po_number: emptyToNull(values.supplier_po_number),
  heat_number: emptyToNull(values.heat_number),
  part_number: values.part_number.trim(),
  part_description: values.part_description.trim(),
  part_revision_level: emptyToNull(values.part_revision_level),
  date_of_completion: values.date_of_completion.format('YYYY-MM-DD'),
  quantity: Number(values.quantity),
  qc_engineer: values.qc_engineer.trim(),
  notes: emptyToNull(values.notes),
});

const mapRecordToFormValues = (
  record: CertificateOfConformityRecord
): CertificateOfConformityFormValues => ({
  document_number: record.document_number,
  document_revision: record.document_revision || undefined,
  date: dayjs(record.date),
  job_card_number: record.job_card_number,
  po_number: record.po_number || undefined,
  job_number: record.job_number || undefined,
  supplier_po_number: record.supplier_po_number || undefined,
  heat_number: record.heat_number || undefined,
  part_number: record.part_number,
  part_description: record.part_description,
  part_revision_level: record.part_revision_level || undefined,
  date_of_completion: dayjs(record.date_of_completion),
  quantity: Number(record.quantity),
  qc_engineer: record.qc_engineer,
  notes: record.notes || undefined,
});

const CertificateOfConformityPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CertificateOfConformityFormValues>();
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CertificateOfConformityRecord | null>(null);
  const [isGeneratingDocumentNumber, setIsGeneratingDocumentNumber] = useState(false);
  const companyId = useAuthStore((s) => s.user?.company_id);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['quality', 'certificates-of-conformity', searchText],
    queryFn: async () => {
      const response = await certificateOfConformityService.getAll(searchText);
      return response.data?.data || response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: CertificateOfConformityPayload) => {
      const response = await certificateOfConformityService.create(payload);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'certificates-of-conformity'] });
      message.success('Certificate of Conformity created successfully');
      setIsModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create Certificate of Conformity');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: CertificateOfConformityPayload;
    }) => {
      const response = await certificateOfConformityService.update(id, payload);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'certificates-of-conformity'] });
      message.success('Certificate of Conformity updated successfully');
      setIsModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update Certificate of Conformity');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await certificateOfConformityService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'certificates-of-conformity'] });
      message.success('Certificate of Conformity deleted successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete Certificate of Conformity');
    },
  });

  const openCreateModal = useCallback(async (cloneSource?: CertificateOfConformityRecord) => {
    setEditingRecord(null);
    form.resetFields();

    form.setFieldsValue(
      cloneSource
        ? {
            ...mapRecordToFormValues(cloneSource),
            document_number: '',
          }
        : {
            date: dayjs(),
            date_of_completion: dayjs(),
            quantity: 1,
          }
    );
    setIsModalOpen(true);

    if (!companyId) {
      return;
    }

    setIsGeneratingDocumentNumber(true);
    try {
      const documentNumber = await settingsService.previewDocumentNumber(companyId, 'COC');
      form.setFieldValue('document_number', documentNumber);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to preview document number');
    } finally {
      setIsGeneratingDocumentNumber(false);
    }
  }, [companyId, form]);

  const columns: ColumnsType<CertificateOfConformityRecord> = useMemo(
    () => [
      {
        title: 'Document Number',
        dataIndex: 'document_number',
        key: 'document_number',
        width: 160,
      },
      {
        title: 'Revision',
        dataIndex: 'document_revision',
        key: 'document_revision',
        width: 120,
        render: (value?: string | null) => value || '—',
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 120,
        render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
      },
      {
        title: 'Job Card Number',
        dataIndex: 'job_card_number',
        key: 'job_card_number',
        width: 160,
      },
      {
        title: 'Part Number',
        dataIndex: 'part_number',
        key: 'part_number',
        width: 150,
      },
      {
        title: 'Part Description',
        dataIndex: 'part_description',
        key: 'part_description',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 120,
        render: (value: number) => Number(value || 0).toLocaleString(),
      },
      {
        title: 'QC Engineer',
        dataIndex: 'qc_engineer',
        key: 'qc_engineer',
        width: 180,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 300,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingRecord(record);
                form.setFieldsValue(mapRecordToFormValues(record));
                setIsModalOpen(true);
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                void openCreateModal(record);
              }}
            >
              Clone
            </Button>
            <Button
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => navigate(`/print/qms/certificates-of-conformity/${record.id}`)}
            >
              Print
            </Button>
            <Popconfirm
              title="Delete Certificate of Conformity?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteMutation.mutate(record.id)}
            >
              <Button danger size="small" icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteMutation.isPending, form, navigate, openCreateModal]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Certificate of Conformity
          </Title>
          <Text type="secondary">
            Maintain Certificate of Conformity records with the same QMS register pattern used across the repo.
          </Text>
        </div>

        <Card>
          <Space
            wrap
            style={{
              marginBottom: 16,
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Search
              allowClear
              placeholder="Search document number, job card, part number, QC engineer..."
              style={{ width: 340 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={() => void openCreateModal()}>
              Add Certificate of Conformity
            </Button>
          </Space>

          <Table<CertificateOfConformityRecord>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={records}
            scroll={{ x: 1400 }}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          />
        </Card>
      </Space>

      <Modal
        title={editingRecord ? 'Edit Certificate of Conformity' : 'Add Certificate of Conformity'}
        open={isModalOpen}
        width={900}
        okText={editingRecord ? 'Update' : 'Create'}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        onOk={() => form.submit()}
      >
        <Form<CertificateOfConformityFormValues>
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            if (editingRecord) {
              const payload = mapFormToPayload(values, { includeDocumentNumber: true });
              await updateMutation.mutateAsync({ id: editingRecord.id, payload });
            } else {
              const payload = mapFormToPayload(values, { includeDocumentNumber: true });
              await createMutation.mutateAsync(payload);
            }
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="document_number"
                label="Document Number"
                rules={[{ required: true, message: 'Please enter document number' }]}
              >
                <Input
                  placeholder="Document number will be previewed from document sequences"
                  readOnly={!editingRecord}
                  suffix={isGeneratingDocumentNumber ? <LoadingOutlined spin /> : null}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="document_revision" label="Document Revision">
                <Input placeholder="e.g., R1, Rev A" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="date"
                label="Date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="job_card_number"
                label="Job Card Number"
                rules={[{ required: true, message: 'Please enter job card number' }]}
              >
                <Input placeholder="Enter job card number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="po_number" label="PO Number">
                <Input placeholder="Enter PO number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="job_number" label="Job Number">
                <Input placeholder="Enter job number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="supplier_po_number" label="Supplier PO Number">
                <Input placeholder="Enter supplier PO" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="heat_number" label="Heat Number">
                <Input placeholder="Enter heat number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="part_number"
                label="Part Number"
                rules={[{ required: true, message: 'Please enter part number' }]}
              >
                <Input placeholder="Enter part number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="part_description"
                label="Part Description"
                rules={[{ required: true, message: 'Please enter part description' }]}
              >
                <Input placeholder="Enter part description" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="part_revision_level" label="Part Revision Level">
                <Input placeholder="Enter revision level" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="date_of_completion"
                label="Date of Completion"
                rules={[{ required: true, message: 'Please select date of completion' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <InputNumber min={0.0001} precision={4} style={{ width: '100%' }} placeholder="Enter quantity" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="qc_engineer"
                label="QC Engineer"
                rules={[{ required: true, message: 'Please enter QC engineer name' }]}
              >
                <Input placeholder="Enter QC engineer name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={4} placeholder="Enter notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CertificateOfConformityPage;
